import React, { useEffect, useMemo, useRef, useState } from 'react';
import SearchBarCTAHeader from './common/SearchBarCTAHeader';
import Table from './common/Table';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import { LoadingErrorState } from './LoadingErrorState';
import { getJobApplications } from '../api/queries';
import { jobApplicationStatusOptions } from '../constants';
import { SidebarFilters } from './common/FilterSidebar';

interface TableProps<T> extends AgGridReactProps<T> {
  ctas?: {
    enableExportToCsv?: boolean;
    custom?: React.ReactNode;
    refresh?: {
      enabled: boolean;
      handler: () => void;
    };
    onClick?: () => void;
  };
  mode?: 'static' | 'server';
  isLoading?: boolean;
  isFetching?: boolean;
  error?: Error | null;
  refetch?: () => void;
  headerTitle?: string;
  description?: string;
  enableRefresh?: boolean;
  storageKey?: string;
  showCategoryFilter?: boolean;
  uniqueCategories?: string[];
  onCategorySelect?: (category: string | null) => void;
  selectedCategory?: string | null;
  showAssignmentFilter?: boolean;
  onAssignmentSelect?: (filter: 'assigned' | 'unassigned') => void;
  selectedAssignment?: 'assigned' | 'unassigned';
  showStatusFilter?: boolean;
  allStatuses?: { value: string; label: string }[];
  selectedStatus?: string | string[];
  onStatusSelect?: (status: string) => void;
  tableRef?: React.RefObject<any>;
  initialSearch?: string;
  sidebarFilters?: SidebarFilters;
  onDistinctIndustries?: (industries: string[]) => void;
  onFilterCounts?: (counts: Record<string, Record<string, number>>) => void;
  isFilterOpen?: boolean;
  onToggleFilter?: () => void;
  activeFilterCount?: number;
}

const TableWithSearchCTA = <T extends Record<string, any>>({
  headerTitle,
  description,
  ctas = {},
  mode = 'static',
  isLoading = false,
  isFetching = false,
  error,
  refetch = () => {},
  enableRefresh = false,
  storageKey,
  showCategoryFilter = false,
  uniqueCategories = [],
  onCategorySelect,
  selectedCategory,
  showAssignmentFilter = false,
  onAssignmentSelect,
  selectedAssignment,
  showStatusFilter = false,
  onStatusSelect,
  selectedStatus,
  allStatuses,
  tableRef,
  initialSearch = '',
  sidebarFilters,
  onDistinctIndustries,
  onFilterCounts,
  isFilterOpen,
  onToggleFilter,
  activeFilterCount,
  ...agGridProps
}: TableProps<T>) => {
  const gridRef = tableRef || useRef<AgGridReact<T>>(null);
  const [search, setSearch] = useState(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  // Only fetch counts on the first request after filters/search/category change, not on every scroll
  const needsCountsRef = useRef(true);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());

      if (mode === 'static') {
        gridRef.current?.api.setGridOption('quickFilterText', search);
      } else {
        gridRef.current?.api.paginationGoToFirstPage();
      }
    }, 400);
    return () => clearTimeout(t);
  }, [search, mode]);


  const dataSource = useMemo(() => {
    if (mode !== 'server') return undefined;

    // New dataSource means filters/search/category changed — refresh counts on next fetch
    needsCountsRef.current = true;

    return {
      getRows: async (params: any) => {
        try {
          gridRef.current?.api.showLoadingOverlay();

          const fetchCounts = needsCountsRef.current;

          const payload = {
            startRow: params.startRow,
            endRow: params.endRow,
            filterModel: params.filterModel,
            sortModel: params.sortModel,
            search: debouncedSearch,

            category: selectedCategory || '',
            status: '',
            includeStatusCounts: fetchCounts,
            includeFilterCounts: fetchCounts,
            ...(sidebarFilters && {
              industries: sidebarFilters.industry,
              languages: sidebarFilters.language,
              applicationStatuses: sidebarFilters.applicationStatus,
              screeningStatuses: sidebarFilters.screeningStatus,
            }),
          };

          const result = await getJobApplications(payload);

          if (fetchCounts) {
            needsCountsRef.current = false;

            // Update status counts for all statuses
            if (result.statusCounts) {
              setStatusCounts(result.statusCounts);
            }

            if (result.distinctIndustries && onDistinctIndustries) {
              onDistinctIndustries(result.distinctIndustries);
            }

            if (result.filterCounts && onFilterCounts) {
              onFilterCounts(result.filterCounts);
            }
          }

          if (result.rows.length === 0) {
            gridRef.current?.api.showNoRowsOverlay();
          } else {
            gridRef.current?.api.hideOverlay();
          }

          params.successCallback(result.rows, result.lastRow);
        } catch (err) {
          console.error('Failed to fetch applications', err);
          gridRef.current?.api.showNoRowsOverlay();
          params.failCallback();
        }
      },
    };
  }, [debouncedSearch, selectedCategory, sidebarFilters]);

  const handleCategoryClick = (category: string) => {
    if (onCategorySelect) {
      onCategorySelect(category === '' ? null : category);
    }
  };

  const handleAssignmentClick = (assignment: string) => {
    if (onAssignmentSelect && assignment !== '') {
      onAssignmentSelect(assignment as 'assigned' | 'unassigned');
    }
  };

  const handleStatusClick = (status: string) => {
    if (onStatusSelect) {
      onStatusSelect(status === '' ? '' : status);
    }
  };

  const isStatic = mode === 'static';

  if (isLoading || isFetching || error || (!agGridProps.rowData && isStatic)) {
    return (
      <LoadingErrorState
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        refetch={refetch}
        data={agGridProps.rowData}
      />
    );
  }

  return (
    <div className="h-full flex flex-col">
      <SearchBarCTAHeader
        tableRef={gridRef}
        ctas={{
          ...ctas,
          refresh: enableRefresh
            ? {
                enabled: enableRefresh,
                handler: refetch,
              }
            : undefined,
        }}
        title={headerTitle}
        description={description}
        filters={{
          categories: {
            enabled: showCategoryFilter,
            options: uniqueCategories.map((cat) => ({
              value: cat,
              label: cat.charAt(0).toUpperCase() + cat.slice(1),
            })),
            selected: selectedCategory || '',
            onChange: handleCategoryClick,
          },
          assignments: {
            enabled: showAssignmentFilter,
            options: [
              { value: 'assigned', label: 'Assigned' },
              { value: 'unassigned', label: 'Unassigned' },
            ],
            selected: selectedAssignment || '',
            onChange: handleAssignmentClick,
          },
          status: {
            enabled: showStatusFilter,
            options: jobApplicationStatusOptions.map((option) => ({
              ...option,
              count:
                option.value === ''
                  ? Object.values(statusCounts).reduce((sum, c) => sum + c, 0)
                  : statusCounts[option.value] ?? 0,
            })),
            selected:
              Array.isArray(selectedStatus) && selectedStatus.length === 0
                ? ''
                : selectedStatus || [],
            onChange: handleStatusClick,
          },
        }}
        search={search}
        setSearch={setSearch}
        isFilterOpen={isFilterOpen}
        onToggleFilter={onToggleFilter}
        activeFilterCount={activeFilterCount}
      />
      

      {/* Status Filter */}
      {allStatuses && (
        <div className="flex flex-wrap gap-2 border mb-2 px-2 py-1 rounded-md w-fit">
          {allStatuses.map((status) => (
            <div
              key={status.value}
              onClick={() => onStatusSelect?.(status.value)}
              className={`px-2 py-1 text-sm rounded-md cursor-pointer transition-colors ${
                (Array.isArray(selectedStatus) ? selectedStatus.includes(status.value) : selectedStatus === status.value)
                  ? 'bg-blue-600 text-white'
                  : 'hover:bg-greyThirty'
              }`}
            >
              {status.label}
            </div>
          ))}
        </div>
      )}

      <Table
        {...agGridProps}
        tableRef={gridRef}
        storageKey={storageKey}
        mode={mode}
        datasource={mode === 'server' ? dataSource : undefined}
      />
    </div>
  );
};

export default TableWithSearchCTA;
